/**
 * Chat-scope chip — the user-controlled "what's inside this chat?"
 * affordance that sits in the composer area on multi-board projects.
 *
 * Why this exists
 * ───────────────
 * The agent's context is `(project_id, board_ids?)` per
 * docs/project-board-model.md §Agent context. Without a visible chip,
 * the user has no way to tell if the agent is reasoning over the
 * whole project or just one PCB — and the answer wasn't reliably
 * derivable from sidebar / URL state, which is why the user kept
 * hitting "agent thought it was scoped to one board".
 *
 * Three states the chip exposes:
 *
 *   • EMPTY   — whole project (every member board's connectors +
 *               brief inline; tools fan out across every PCB's
 *               current version).
 *   • [bid]   — single PCB. Agent is pinned; tools resolve only that
 *               board's version.
 *   • [a, b…] — explicit subset. Anything narrower than "all" but
 *               wider than "one".
 *
 * Stored in `useChatStore.activeChatBoardIds` so it's independent
 * from the navigational `activeBoardId` (which PCB the user is
 * looking at). Clicking around the sidebar doesn't silently
 * change the chat's scope; only the chip does.
 *
 * Hidden on single-board projects — there's nothing to pick.
 */
import { useProjects } from "@features/projects/hooks/useProjects";
import type { Project } from "@models/project";
import CheckIcon from "@mui/icons-material/CheckRounded";
import ExpandIcon from "@mui/icons-material/ExpandMoreRounded";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useChatStore } from "@stores/chatStore";
import { useProjectStore } from "@stores/projectStore";
import { useMemo, useState } from "react";

interface ChatScopeChipProps {
  /** Resolved project record from the project store / shell. The
   *  chip reads its boards list and renders names. */
  project: Project | null | undefined;
}

export function ChatScopeChip({ project }: ChatScopeChipProps) {
  const activeChatBoardIds = useChatStore((s) => s.activeChatBoardIds);
  const setActiveChatBoardIds = useChatStore((s) => s.setActiveChatBoardIds);

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  // Sort boards alphabetically with numeric awareness so "Board 2"
  // sits before "Board 10" — same comparator the rest of the app
  // uses for consistent ordering across surfaces.
  const boards = useMemo(() => {
    if (!project?.boards) return [];
    return [...project.boards].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    );
  }, [project]);

  // Hide on single-board projects — there's nothing to pick.
  if (boards.length < 2) return null;

  const total = boards.length;
  const selectedSet = new Set(activeChatBoardIds);
  const inProject = activeChatBoardIds.length === 0;
  const selectedBoards = boards.filter((b) => selectedSet.has(b.id));
  const allSelected = inProject || selectedBoards.length === total;

  // Label: "All PCBs · 3" for project-scope, single-name when
  // exactly one is picked, comma-list-with-counter when subset.
  const label = inProject
    ? `All PCBs · ${total}`
    : selectedBoards.length === 1
      ? (selectedBoards[0]?.name ?? "1 PCB")
      : `${selectedBoards.map((b) => b.name).join(", ")} · ${selectedBoards.length} of ${total}`;

  const handleToggle = (boardId: string) => {
    // In project-scope, every board is implicitly included — clicking a
    // ticked board flips to "all-but-this-one" by materialising the
    // explicit list of every *other* board.
    if (inProject) {
      const next = boards.map((b) => b.id).filter((id) => id !== boardId);
      setActiveChatBoardIds(next);
      return;
    }
    if (selectedSet.has(boardId)) {
      // Removing the only entry collapses to project-scope.
      const next = activeChatBoardIds.filter((id) => id !== boardId);
      setActiveChatBoardIds(next);
    } else {
      const next = [...activeChatBoardIds, boardId];
      // If the user just re-checked the last board, collapse back to
      // project-scope so newly added boards stay covered.
      if (next.length === total) {
        setActiveChatBoardIds([]);
      } else {
        setActiveChatBoardIds(next);
      }
    }
  };

  const handleSelectAll = () => {
    // Project-scope is the canonical "all" representation — the
    // server treats `[]` the same as omitted, and there's no
    // semantic difference between "all member boards explicitly
    // listed" and "no list, default to project-scope". Storing []
    // keeps that contract clean and avoids the chip drifting out
    // of sync if a board is later added to the project.
    setActiveChatBoardIds([]);
    setAnchor(null);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={<LayersIcon sx={{ fontSize: 16 }} />}
        endIcon={<ExpandIcon sx={{ fontSize: 16 }} />}
        sx={(t) => ({
          textTransform: "none",
          fontWeight: 500,
          letterSpacing: "-0.005em",
          fontSize: "0.78125rem",
          // Editorial-gold treatment because the chip is a
          // user-judgement signal (which boards are *in* this
          // conversation) — the same channel the wallet / brief
          // editing chips use.
          color: t.palette.editorial.contrastText,
          borderColor: alpha(t.palette.editorial.main, 0.4),
          bgcolor: alpha(t.palette.editorial.main, 0.06),
          "&:hover": {
            borderColor: alpha(t.palette.editorial.main, 0.6),
            bgcolor: alpha(t.palette.editorial.main, 0.1),
          },
          // Truncate when the label gets long (subset of many).
          maxWidth: { xs: "60%", sm: 360 },
          "& .MuiButton-startIcon": { mr: 0.625 },
          "& .MuiButton-endIcon": { ml: 0.5 },
        })}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        aria-label="Pick chat scope"
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {label}
        </Box>
      </Button>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: { sx: { minWidth: 260, maxWidth: 360 } },
        }}
      >
        {/* "All PCBs" — radio-style toggle that clears the subset.
            Distinguished visually so the user reads "this is the
            default" without parsing checkboxes. */}
        <MenuItem
          onClick={handleSelectAll}
          selected={allSelected}
          sx={{ py: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            {allSelected && <CheckIcon sx={{ fontSize: 16 }} />}
          </ListItemIcon>
          <ListItemText
            primary="All PCBs"
            secondary={`Project scope · ${total} boards`}
            primaryTypographyProps={{
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
            secondaryTypographyProps={{
              fontSize: "0.6875rem",
              color: "text.secondary",
            }}
          />
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ px: 2, pt: 0.5, pb: 0.75 }}>
          <Typography
            sx={(t) => ({
              fontFamily: t.fonts.mono,
              fontSize: "0.625rem",
              fontWeight: 500,
              color: "text.secondary",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
            })}
          >
            Specific PCBs
          </Typography>
        </Box>
        {boards.map((b) => {
          const checked = selectedSet.has(b.id);
          return (
            <MenuItem
              key={b.id}
              onClick={() => handleToggle(b.id)}
              sx={{ py: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox
                  edge="start"
                  size="small"
                  checked={inProject || checked}
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: 0.25 }}
                />
              </ListItemIcon>
              <ListItemText
                primary={b.name}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              />
            </MenuItem>
          );
        })}
        <Divider sx={{ mt: 0.5 }} />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            Pick one or more PCBs to scope the agent. Empty = whole project.
          </Typography>
        </Box>
      </Menu>
    </>
  );
}

/**
 * Pull the active project from the project store + projects list so
 * the chip can render board names without each consumer re-doing
 * the lookup.
 */
export function ChatScopeChipFromStore() {
  const activeProjectId = useProjectStore((s) => s.activeProject);
  const { data: projects } = useProjects();
  const project = useMemo(
    () => projects?.find((p) => p.id === activeProjectId) ?? null,
    [projects, activeProjectId],
  );
  if (!project) return null;
  return <ChatScopeChip project={project} />;
}

/**
 * Same chip, but the consumer hands in the resolved project (used
 * by the project-shell rail surface where the project is already in
 * scope).
 */
export type { ChatScopeChipProps };
